import React, { useState, useEffect, useRef } from "react";
import styles from "./FeatureDemo.module.css";

import bookingIcon from "../../../assets/svg/ai-sales-landing/booking_modification.svg";
import proactiveIcon from "../../../assets/svg/ai-sales-landing/proactive_sales.svg";
import refundIcon from "../../../assets/svg/ai-sales-landing/refund.svg";
import specialRequestIcon from "../../../assets/svg/ai-sales-landing/special_request.svg";

import bookingActiveIcon from "../../../assets/svg/ai-sales-landing/booking_active.svg";
import proactiveActiveIcon from "../../../assets/svg/ai-sales-landing/proactive_active.svg";
import refundActiveIcon from "../../../assets/svg/ai-sales-landing/refund_active.svg";
import specialRequestActiveIcon from "../../../assets/svg/ai-sales-landing/special_request_active.svg";

const FeatureDemo = () => {
  const [activeFeatureIndex, setActiveFeatureIndex] = useState(0);
  const [previousIndex, setPreviousIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const contentRef = useRef(null);
  const [isVideoLoading, setIsVideoLoading] = useState(true);

  const features = [
    {
      title: "Book in a snap",
      description:
        "Guests can check availability and confirm bookings in seconds — no forms, no hold times, just a simple voice or text command.",
      content: "",
      icon: bookingIcon,
      activeIcon: bookingActiveIcon,
      activeVideo: "booking_create_snap.mov",
    },
    {
      title: "Proactive Sales",
      description:
        "Turn interest into bookings — our AI reaches out with timely, personalized offers to guests who are ready to convert.",
      icon: proactiveIcon,
      activeIcon: proactiveActiveIcon,
      activeVideo: "feature_video_615_frame.mov",
    },
    {
      title: "Booking Modification",
      icon: bookingIcon,
      description:
        "Need to change a date or guest detail? The AI handles it instantly over chat or call, with zero wait and no confusion.",
      activeIcon: bookingActiveIcon,
      activeVideo: "frame_624.mov",
    },
    {
      title: "Refund Status Check",
      icon: refundIcon,
      description:
        "Guests can track their refund status anytime, without calling the front desk — keeping them informed and reassured, 24/7.",
      activeIcon: refundActiveIcon,
      activeVideo: "frame_604_feature.mov",
    },
    {
      title: "Special Request Handling",
      icon: specialRequestIcon,
      description:
        "Guests share their special requests, such as extra bedding or dietary needs, and the voicebot ensures they reach the hotel staff.",
      activeIcon: specialRequestActiveIcon,
      activeVideo: "frame_623_feature.mov",
    },
  ];

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 980);
    };

    checkMobile();

    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    if (isMobile && contentRef.current) {
      setTimeout(() => {
        const activeElement = document.querySelector(
          `.${styles.activeFeature}`
        );
        if (activeElement) {
          activeElement.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 100);
    }
  }, [activeFeatureIndex, isMobile]);

  const handleFeatureClick = (index) => {
    if (index !== activeFeatureIndex && !isAnimating) {
      setPreviousIndex(activeFeatureIndex);
      setActiveFeatureIndex(index);
      setIsAnimating(true);
      setTimeout(() => {
        setIsAnimating(false);
      }, 500);
    }
  };

  useEffect(() => {
    setActiveFeatureIndex(0);
  }, []);

  const animationDirection =
    previousIndex < activeFeatureIndex ? "right" : "left";

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>
          Let AI Answer — Calls, Chats, and{" "}
          <span className={styles.highlight}>Everything</span> In Between
        </h1>
      </div>

      <div className={styles.contentContainer} ref={contentRef}>
        <div className={styles.featuresColumn}>
          {features.map((feature, index) => (
            <div
              key={index}
              className={`${styles.featureItem} ${
                index === activeFeatureIndex ? styles.activeFeature : ""
              }`}
            >
              <div
                className={styles.featureHeader}
                onClick={() => handleFeatureClick(index)}
              >
                <div className={styles.iconContainer}>
                  <img
                    src={
                      index === activeFeatureIndex
                        ? feature.activeIcon
                        : feature.icon
                    }
                    alt={feature.title}
                    className={styles.featureIcon}
                  />
                </div>
                <h3 className={styles.featureTitle}>{feature.title}</h3>
              </div>

              {index === activeFeatureIndex && (
                <div className={styles.featureDescription}>
                  <p>{feature.description}</p>
                </div>
              )}

              {index === activeFeatureIndex && (
                <div className={styles.divider}></div>
              )}
            </div>
          ))}
        </div>
        <div className={styles.contentColumn}>
          <div
            className={`${styles.contentDisplay} ${
              isAnimating
                ? styles[
                    `slide${animationDirection === "right" ? "In" : "Out"}`
                  ]
                : ""
            }`}
          >
            <div className={styles.placeholderContent}>
              {isVideoLoading && (
                <div className={styles.loader}>
                  <div className={styles.spinner}></div>
                </div>
              )}
              <video
                src={`https://ik.imagekit.io/hotelzifyprod/${features[activeFeatureIndex]?.activeVideo}`}
                alt={features[activeFeatureIndex]?.title}
                style={{ width: "100%", height: "96%", borderRadius: "12%" }}
                autoPlay={true}
                loop={true}
                playsInline={true}
                muted={true}
                onLoadStart={() => setIsVideoLoading(true)}
                onLoadedData={() => setIsVideoLoading(false)}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeatureDemo;
